/**
 * FAConsolidationStrip — Phase D, FA-specific.
 *
 * Different from the generic ``ConsolidationStrip`` because Fixed Assets
 * has explicit divisional sign-off (Ready / Ready-No-Activity) and a
 * real "Generate Consolidated" action that composes the per-division
 * runs into the consolidation run.
 *
 * Shown on the FA Landing when:
 *   • multi-division client (≥2 divisions), AND
 *   • current URL scope = consolidation, AND
 *   • the user is on the no-run state OR has selected the consolidation
 *     run from the list.
 *
 * Drives:
 *   • Per-division progress tiles with explicit state pills (Not Started /
 *     In Progress / Ready / No Activity).
 *   • Click "Open" on a tile → navigates to that division's FA run
 *     (creating it on-the-fly if it doesn't exist yet).
 *   • "Generate Consolidated" button — enabled only when every division
 *     is in a final state; calls POST /compose-from-divisions.
 *   • "Stale" warning when a division has signed off after the last
 *     composition — auditor needs to re-compose.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, ArrowRight, CheckCircle, CircleDashed, ClockCounterClockwise, Warning } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { toast } from "sonner";


const STATE_PILL = {
  not_started:       { label: "Not Started",   cls: "border-slate-300 bg-slate-50 text-slate-700",     Icon: CircleDashed },
  in_progress:       { label: "In Progress",   cls: "border-amber-300 bg-amber-50 text-amber-900",     Icon: ClockCounterClockwise },
  ready:             { label: "Ready",         cls: "border-emerald-300 bg-emerald-50 text-emerald-800", Icon: CheckCircle },
  ready_no_activity: { label: "No Activity",   cls: "border-sky-300 bg-sky-50 text-sky-800",           Icon: CheckCircle },
};


export default function FAConsolidationStrip({
  clientId, fy, divisions, scope, consolidationRunId, onComposed,
}) {
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composing, setComposing] = useState(false);

  const isConsolidationOnMulti =
    scope?.scopeKind === "consolidation" &&
    Array.isArray(divisions) && divisions.length >= 2;

  const refresh = useCallback(async () => {
    if (!isConsolidationOnMulti || !consolidationRunId) return;
    setLoading(true);
    try {
      const { data } = await http.get(`/fixed-assets/runs/${consolidationRunId}/composition-readiness`);
      setReadiness(data);
    } catch (e) {
      // Run probably doesn't exist yet — show empty state.
      setReadiness(null);
    } finally {
      setLoading(false);
    }
  }, [isConsolidationOnMulti, consolidationRunId]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Create (or jump to) a division-scoped FA run for the given division. */
  const openDivision = async (div) => {
    if (div.run_id) {
      navigate(`/dashboard/clients/${clientId}/utilities/fixed-assets/runs/${div.run_id}?scope=div_${div.division_id}&fy=${fy}`);
      return;
    }
    // No run yet → create one at division scope.
    try {
      const { data } = await http.post(`/fixed-assets/runs`, {
        client_id: clientId, fy,
        scope_kind: "division",
        division_ids: [div.division_id],
      });
      navigate(`/dashboard/clients/${clientId}/utilities/fixed-assets/runs/${data.id}?scope=div_${div.division_id}&fy=${fy}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not open ${div.division_name}`);
    }
  };

  const onGenerate = async () => {
    if (!consolidationRunId) return;
    setComposing(true);
    try {
      const { data } = await http.post(`/fixed-assets/runs/${consolidationRunId}/compose-from-divisions`);
      toast.success(
        `Composed ${data.ledgers} ledgers · ${data.additions} additions · ` +
        `${data.credits} credits · ${data.block_opening_rows} blocks from ` +
        `${data.sources.length} division(s).`
      );
      onComposed?.();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Composition failed");
    } finally {
      setComposing(false);
    }
  };

  if (!isConsolidationOnMulti) return null;

  const stale = readiness?.compose_stale;
  const ready = readiness?.ready_to_compose;
  const hasComposed = !!readiness?.last_composed_at;

  return (
    <div data-testid="fa-consolidation-strip" className="mb-4 border border-emerald-200 bg-emerald-50/40 rounded-sm">
      <div className="px-4 py-2.5 border-b border-emerald-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Stack size={14} className="text-emerald-700" weight="bold" />
          <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-emerald-900">
            Consolidation View · FY {fy} · {divisions.length} divisions
            {readiness && (
              <span className="ml-2 text-emerald-700/80 normal-case tracking-normal">
                · {readiness.n_final}/{readiness.n_divisions} finalised
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {stale && hasComposed && (
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-amber-900 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
              <Warning size={11} weight="bold"/> Stale — re-compose
            </span>
          )}
          <button
            type="button"
            data-testid="fa-generate-consolidated-btn"
            disabled={!ready || composing || !consolidationRunId}
            onClick={onGenerate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 bg-white text-[11.5px] font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed rounded-sm"
            title={
              !consolidationRunId ? "Create a Consolidation run first."
              : !ready ? "Block(s) pending: each division must be marked Ready or No Activity."
              : composing ? "Composing…"
              : "Compose all finalised division runs into this consolidation run."
            }
          >
            <ArrowRight size={11}/> {composing ? "Composing…" : hasComposed ? "Re-compose" : "Generate Consolidated"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="px-4 py-6 text-center text-[12px] text-emerald-900/70 font-mono">
          Loading per-division status…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
          {(readiness?.divisions || divisions.map(d => ({
            division_id: d.division_id, division_name: d.name, status: "not_started", run_id: null,
          }))).map((d) => {
            const pill = STATE_PILL[d.status] || STATE_PILL.not_started;
            const Icon = pill.Icon;
            return (
              <div
                key={d.division_id}
                data-testid={`fa-conso-div-${d.division_id}`}
                className="bg-white border border-emerald-100 hover:border-emerald-300 transition rounded-sm px-3 py-2.5"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-medium text-[12.5px] text-slate-900 truncate">{d.division_name}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 ${pill.cls}`}>
                    <Icon size={10} weight="bold"/> {pill.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => openDivision(d)}
                    className="text-[11px] text-sky-700 hover:text-sky-900"
                    data-testid={`fa-conso-div-${d.division_id}-open`}
                  >
                    {d.run_id ? "Open →" : "Start →"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
